// Colour helpers for the logo generator: parsing, mixing, harmonies, contrast and the values a brand guide lists (RGB, CMYK).

export function normaliseHex(value, fallback = "#000000") {
  let text = String(value || "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(text)) text = text.split("").map((c) => c + c).join("");
  return /^[0-9a-f]{6}$/i.test(text) ? `#${text.toLowerCase()}` : fallback;
}

export function hexToRgb(hex) {
  const h = normaliseHex(hex).slice(1);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function rgbToHex([r, g, b]) {
  const part = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

export function hexToHsl(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s, l];
}

export function hslToHex([h, s, l]) {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(1, s));
  l = Math.max(0, Math.min(1, l));
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return rgbToHex([(r + m) * 255, (g + m) * 255, (b + m) * 255]);
}

// amount 0..1 of `other` mixed into `hex`
export function mix(hex, other, amount) {
  const a = hexToRgb(hex);
  const b = hexToRgb(other);
  return rgbToHex(a.map((v, i) => v + (b[i] - v) * amount));
}

export const lighten = (hex, amount) => mix(hex, "#ffffff", amount);
export const darken = (hex, amount) => mix(hex, "#000000", amount);

export function luminance(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export const isDark = (hex) => luminance(hex) < 0.36;

// Black or white, whichever reads better on `hex`.
export const readableOn = (hex) => (contrast(hex, "#ffffff") >= contrast(hex, "#111111") ? "#ffffff" : "#111111");

export function cmyk(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const k = 1 - Math.max(r, g, b);
  if (k >= 1) return [0, 0, 0, 100];
  return [(1 - r - k) / (1 - k), (1 - g - k) / (1 - k), (1 - b - k) / (1 - k), k].map((v) => Math.round(v * 100));
}

// Colours that go with `hex`: the harmonies designers use.
export function harmonies(hex) {
  const [h, s, l] = hexToHsl(hex);
  const turn = (deg, sat = s, light = l) => hslToHex([h + deg, sat, light]);
  return {
    complementary: [hex, turn(180)],
    analogous: [hex, turn(-30), turn(30)],
    triadic: [hex, turn(120), turn(240)],
    split: [hex, turn(150), turn(210)],
    tints: [hex, lighten(hex, 0.35), darken(hex, 0.35)],
  };
}

// Ready-made palettes: [name, primary, secondary, accent]
export const PALETTES = [
  ["Ocean", "#0b5ed7", "#00b4d8", "#0a2540"],
  ["Midnight", "#1b2a49", "#3d5af1", "#22d1ee"],
  ["Forest", "#1f6f4a", "#7bc47f", "#0d3b2e"],
  ["Sunset", "#f46036", "#ffb703", "#7a1f5c"],
  ["Royal", "#5b21b6", "#a855f7", "#1e1b4b"],
  ["Crimson", "#c1121f", "#f77f00", "#1d1d1d"],
  ["Gold & Black", "#c9a227", "#f1d87a", "#111111"],
  ["Rose", "#e11d74", "#ff8fab", "#4a0d2e"],
  ["Mint", "#10b981", "#6ee7b7", "#064e3b"],
  ["Coffee", "#6f4e37", "#c8a27a", "#2b1a10"],
  ["Slate", "#334155", "#64748b", "#0f172a"],
  ["Electric", "#2563eb", "#f43f5e", "#111827"],
  ["Teal & Coral", "#0f766e", "#fb7185", "#134e4a"],
  ["Navy & Gold", "#14213d", "#fca311", "#e5e5e5"],
  ["Sky", "#38bdf8", "#818cf8", "#0c4a6e"],
  ["Earth", "#a3623b", "#d9b38c", "#3b2a20"],
  ["Lime", "#65a30d", "#bef264", "#1a2e05"],
  ["Plum", "#7e22ce", "#f0abfc", "#3b0764"],
  ["Mono", "#111827", "#6b7280", "#d1d5db"],
  ["Crimson & Navy", "#b91c1c", "#1e3a8a", "#f3f4f6"],
];
